// apps/client/src/state/slices/safety.ts (M2-T08, §8.7, spec §10.1) — your account-level block list, reports, and the helper that hides
// blocked players' chat. Keys are prefixed `safety`.
//
// The block list belongs to an account, not to the device: `safetyFor` records whose list `safetyBlocked` is. While a different account
// (or none) is signed in, the stored list counts as empty and not loaded, so a player who signs in on the same tab after someone else
// never inherits, or sees, that account's blocks, and their own list loads with their first chat line. Read the list through
// `safetyBlockedNow` and `safetyReady`, not the raw fields.
import type { ChatLine, ReportRequest, ResidentId, ResidentPublic, UserId } from '@whiskerton/shared';
import { api } from '../../api/endpoints';
import type { GameStore, SliceCreator } from '../types';

type Blocked = Record<string, { name: string; at: number }>;         // keyed by UserId

export interface SafetySlice {
  safetyBlocked: Blocked;
  safetyLoaded: boolean;
  /** The account (`me.user.id`) the stored list belongs to; null before any sign-in. */
  safetyFor: UserId | null;
  safetyLoad(): Promise<void>;
  safetyBlock(userId: UserId, name: string): Promise<void>;
  safetyUnblock(userId: UserId): Promise<void>;
  safetyReport(req: ReportRequest): Promise<boolean>;               // toast on success / failure
  safetyIsBlockedResident(r: ResidentId): boolean;
  /** Forget the block list (sign-out, account deletion). */
  safetyReset(): void;
}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging into GameStore
declare module '../types' { interface GameStore extends SafetySlice {} }

const NONE: Readonly<Blocked> = Object.freeze({});
const owner = (s: GameStore): UserId | null => s.me?.user.id ?? null;
const current = (s: GameStore): boolean => s.safetyFor === owner(s);
/** The signed-in account's block list ({} while the stored list belongs to another account). A stable reference, safe as a selector. */
export const safetyBlockedNow = (s: GameStore): Readonly<Blocked> => current(s) ? s.safetyBlocked : NONE;
/** Whether the signed-in account's block list has been loaded. */
export const safetyReady = (s: GameStore): boolean => s.safetyLoaded && current(s);

/** The chat lines to show: every line except those whose author's account you blocked. */
export function visibleChatLines(lines: readonly ChatLine[], residents: Readonly<Record<string, ResidentPublic>>, blocked: Readonly<Record<string, unknown>>): ChatLine[] {
  return lines.filter((l) => { const u = residents[l.r]?.userId; return !u || !(u in blocked); });
}

type Entry = Blocked[string];
/** One GET /api/blocks at a time per account (chat lines can arrive together). Blocks and unblocks made while it is in flight are
 *  replayed over its result, so a load that started before them never undoes them. */
let loading: { who: UserId | null; edits: Map<string, Entry | null>; p: Promise<void> } | null = null;

export const slice: SliceCreator<SafetySlice> = (set, get) => {
  /** Apply one local block (an entry) or unblock (null) to `who`'s list, unless another account is signed in by now. */
  const edit = (who: UserId | null, userId: string, entry: Entry | null) => {
    if (owner(get()) !== who) return;
    if (loading?.who === who) loading.edits.set(userId, entry);
    set((s) => {
      const next = { ...safetyBlockedNow(s) };
      if (entry) next[userId] = entry; else delete next[userId];
      return { safetyBlocked: next, safetyLoaded: safetyReady(s), safetyFor: who };
    });
  };
  return {
    safetyBlocked: {},
    safetyLoaded: false,
    safetyFor: null,
    safetyLoad() {
      const who = owner(get());
      if (loading?.who === who) return loading.p;
      const run: NonNullable<typeof loading> = { who, edits: new Map(), p: Promise.resolve() };
      loading = run;
      run.p = api.listBlocks()
        .then(({ blocks }) => {
          if (owner(get()) !== who) return;                            // another account by now: drop the old account's list
          const next: Blocked = Object.fromEntries(blocks.map((b) => [b.userId, { name: b.name, at: b.at }]));
          for (const [u, e] of run.edits) { if (e) next[u] = e; else delete next[u]; }
          set({ safetyBlocked: next, safetyLoaded: true, safetyFor: who });
        })
        .finally(() => { if (loading === run) loading = null; });
      return run.p;
    },
    async safetyBlock(userId, name) {
      const who = owner(get());
      await api.block(userId);
      edit(who, userId, { name, at: Date.now() });
    },
    async safetyUnblock(userId) {
      const who = owner(get());
      await api.unblock(userId);
      edit(who, userId, null);
    },
    async safetyReport(req) {
      try { await api.report(req); get().toast('safety.reportSent'); return true; }
      catch { get().toast('safety.reportFailed', undefined, 'warn'); return false; }
    },
    safetyIsBlockedResident(r) {
      const s = get();
      const u = s.residents[r]?.userId;
      return !!u && u in safetyBlockedNow(s);
    },
    safetyReset() {
      loading = null;
      set({ safetyBlocked: {}, safetyLoaded: false, safetyFor: null });
    },
  };
};
